import { AUTOSCALE_SETTLE_EPSILON, AUTOSCALE_SETTLE_MS } from "../constants";

const ZERO_LAG = { min: 0, max: 0 };

/**
 * Smooth autoscale. The fitted range follows the shown bars directly (the
 * moving last bar already moves it smoothly); it jumps only when other bars
 * come into view (the view slides after a new bar, a gesture). Then the shown
 * range lags behind the new one and the lag fades out exponentially with the
 * time constant AUTOSCALE_SETTLE_MS; further jumps add to the lag, so a
 * continuous gesture moves the shown range towards the target every frame.
 * A snapped frame (new history, no animations) applies the range at once.
 */
export const createAutoscaleMotion = () => {
  let shownRange = null;
  let fittedBars = null;
  // Shown minus target, { min, max }
  let lag = ZERO_LAG;
  let lastTime = 0;

  const reset = () => {
    shownRange = null;
    fittedBars = null;
    lag = ZERO_LAG;
  };

  return {
    isActive: () => lag.min !== 0 || lag.max !== 0,

    /**
     * @param {{ min: number, max: number } | null} targetRange - prices of the visible bars
     * @param {{ first: number, last: number }} visibleBars - their indexes
     * @param {{ isSnapped: boolean, now: number }} params
     * @returns {{ min: number, max: number } | null} range to show
     */
    smooth: (targetRange, visibleBars, { isSnapped, now }) => {
      if (!targetRange) {
        reset();
        return targetRange;
      }

      const hasOtherBars =
        !fittedBars || fittedBars.first !== visibleBars.first || fittedBars.last !== visibleBars.last;

      fittedBars = { first: visibleBars.first, last: visibleBars.last };

      if (isSnapped || !shownRange) {
        lag = ZERO_LAG;
      } else {
        const decay = Math.exp(-Math.max(0, now - lastTime) / AUTOSCALE_SETTLE_MS);
        const from = hasOtherBars
          ? { min: shownRange.min - targetRange.min, max: shownRange.max - targetRange.max }
          : lag;

        lag = { min: from.min * decay, max: from.max * decay };
      }

      const threshold = Math.abs(targetRange.max - targetRange.min) * AUTOSCALE_SETTLE_EPSILON;

      if (Math.abs(lag.min) <= threshold && Math.abs(lag.max) <= threshold) lag = ZERO_LAG;

      lastTime = now;
      shownRange = { min: targetRange.min + lag.min, max: targetRange.max + lag.max };

      return shownRange;
    },
  };
};
